// Shared clinical rule tables for Form A (GLP-1 naive) and Form B (GLP-1 experienced).
// Sourced from "18.9.26 SD Questionnaire logic v4 Form A/B" — both docs use identical
// Q7/Q8/Q10/Q12 lists, so they're defined once here.

export const TIER = {
  GREEN: "GREEN",
  AMBER: "AMBER",
  ORANGE: "ORANGE",
  RED: "RED",
};

// Q7 — safety triage conditions. "orangeCandidate" flags items the backend-summary
// table separately calls out as ORANGE ("Patient Contact Required") rather than the
// plain AMBER the inline question text prints next to them; computeFinalTier can use
// this to upgrade the tier without changing the immediate-reject behaviour (only RED
// blocks in the UI).
export const Q7_CONDITIONS = [
  { key: "t1d", label: "Type 1 diabetes", tier: TIER.AMBER },
  { key: "hyperthyroidism", label: "Hyperthyroidism", tier: TIER.AMBER },
  { key: "diabeticRetinopathy", label: "Diabetic Retinopathy", tier: TIER.AMBER },
  { key: "pancreatitis", label: "Previous pancreatitis", tier: TIER.AMBER, orangeCandidate: true },
  { key: "gallbladder", label: "Gall Bladder disease e.g. gall stones", tier: TIER.AMBER },
  { key: "gastroparesis", label: "Gastroparesis (delayed stomach emptying)", tier: TIER.RED },
  {
    key: "ibdActive",
    label: "Active Crohn's disease or ulcerative colitis, or in remission for less than 6 months",
    tier: TIER.AMBER,
  },
  { key: "severeLiverDisease", label: "Severe liver disease", tier: TIER.AMBER },
  {
    key: "severeKidneyDisease",
    label: "Severe kidney disease (CKD Stage 5 / eGFR < 15ml/min)",
    tier: TIER.AMBER,
  },
  {
    key: "heartFailureAdmission",
    label: "Heart Failure — admitted to hospital because of heart failure in the last 12 months",
    tier: TIER.AMBER,
  },
  {
    key: "activeCancer",
    label: "Active cancer or currently undergoing cancer treatment (e.g., chemotherapy, radiotherapy)",
    tier: TIER.RED,
  },
  { key: "mtcHistory", label: "Personal or family history of Medullary Thyroid Cancer", tier: TIER.RED },
  { key: "men2", label: "Multiple Endocrine Neoplasia Type 2 (MEN2)", tier: TIER.RED },
  {
    key: "recentWeightLossSurgery",
    label:
      "Any weight loss procedures or surgeries in the last 12 months — such as gastric sleeve, gastric band, gastric bypass or gastric balloon",
    tier: TIER.AMBER,
  },
  { key: "eatingDisorder", label: "Eating disorders (anorexia, bulimia, binge eating disorder etc)", tier: null },
  { key: "selfHarm", label: "Self-harm or suicidal thoughts within the last 12 months", tier: TIER.AMBER },
  {
    key: "memoryCondition",
    label: "Any condition that affects your memory or decision making ability e.g. dementia",
    tier: TIER.AMBER,
  },
  { key: "none", label: "None of the above", tier: TIER.GREEN },
];

// Eating disorder sub-type, asked when "eatingDisorder" is ticked in Q7.
export const EATING_DISORDER_TYPES = [
  { key: "anorexia", label: "Anorexia Nervosa", tier: TIER.RED },
  { key: "bulimia", label: "Bulimia Nervosa", tier: TIER.RED },
  { key: "bed", label: "Binge Eating Disorder (BED)", tier: TIER.AMBER },
  { key: "other", label: "Other (please specify)", tier: TIER.AMBER },
];

// Q8 — comorbidities that lower the qualifying BMI threshold. These don't carry their
// own reject/review tier; ticking any of them just flips the "hasComorbidity" flag
// used by getBmiThreshold.
export const Q8_COMORBIDITIES = [
  { key: "t2d", label: "Type 2 Diabetes" },
  { key: "prediabetes", label: "Pre-diabetes / Insulin Resistance (Impaired Glucose Tolerance)" },
  { key: "pcos", label: "Polycystic Ovary Syndrome (PCOS)" },
  { key: "fattyLiver", label: "Fatty Liver Disease" },
  { key: "hypertension", label: "High Blood Pressure (Hypertension)" },
  { key: "highCholesterol", label: "High Cholesterol or High Triglycerides" },
  { key: "cvd", label: "Cardiovascular disease (e.g. past heart attack, angina, coronary heart disease)" },
  { key: "afib", label: "Atrial Fibrillation or irregular heart rhythm" },
  { key: "strokeTia", label: "Stroke or TIA (mini-stroke)" },
  {
    key: "pad",
    label: "Peripheral Arterial Disease (poor leg circulation or previous vascular surgery)",
  },
  { key: "osa", label: "Obstructive Sleep Apnoea" },
  { key: "asthmaCopd", label: "Asthma or Chronic Obstructive Pulmonary Disease (COPD)" },
  { key: "gord", label: "Gastro-Oesophageal Reflux Disease (GORD) or severe chronic acid reflux" },
  { key: "osteoarthritis", label: "Osteoarthritis or chronic weight-bearing joint pain" },
  { key: "backPain", label: "Chronic back pain" },
  { key: "ed", label: "Erectile Dysfunction" },
  { key: "depressionAnxiety", label: "On treatment for Depression or Anxiety linked to your weight" },
  { key: "none", label: "None of the above" },
];

// Q10 — current medications with a known interaction/safety flag.
export const Q10_MEDICATIONS = [
  { key: "none", label: "None of the above", tier: TIER.GREEN },
  { key: "insulin", label: "Insulin", tier: TIER.AMBER },
  {
    key: "sulfonylureas",
    label: "Sulfonylureas (e.g., Gliclazide, Glimepiride, Glipizide)",
    tier: TIER.AMBER,
    orangeCandidate: true,
  },
  {
    key: "dpp4",
    label: "DPP-4 Inhibitors (e.g., Sitagliptin, Linagliptin, Alogliptin)",
    tier: TIER.AMBER,
  },
  {
    key: "sglt2",
    label: "SGLT2 Inhibitors (e.g., Dapagliflozin, Empagliflozin)",
    tier: TIER.AMBER,
    orangeCandidate: true,
  },
  {
    key: "ntiOthers",
    label: "Others — Warfarin, Lithium, Digoxin, Theophylline, Amiodarone, Flecainide, Clozapine",
    tier: TIER.AMBER,
    orangeCandidate: true,
  },
  { key: "orlistat", label: "Orlistat", tier: TIER.AMBER },
];

// Q12 — allergy to the medication classes we prescribe. A "Yes" here is an absolute
// contraindication (Red); "other allergies" is informational only (Amber).
export const Q12_ALLERGY_MEDICATIONS = [
  { key: "semaglutide", label: "Wegovy/Ozempic/Rybelsus (Semaglutide)" },
  { key: "tirzepatide", label: "Mounjaro (Tirzepatide)" },
  { key: "liraglutide", label: "Saxenda/Nevolat/Victoza (Liraglutide)" },
];

// Q13 — GLP-1 medications the "have you ever taken..." follow-up pathway asks about,
// and the dose ladder for each, reused by Form B's own "which medication / what dose"
// questions since both docs specify the same options.
export const GLP1_MEDICATIONS = [
  { key: "mounjaroInjection", label: "Mounjaro injection", doseOptions: ["2.5mg", "5mg", "7.5mg", "10mg", "12.5mg", "15mg"] },
  { key: "wegovyInjection", label: "Wegovy injection", doseOptions: ["0.25mg", "0.5mg", "1mg", "1.7mg", "2.4mg", "7.2mg"] },
  { key: "wegovyTablet", label: "Wegovy tablet", doseOptions: ["1.5mg", "4mg", "9mg", "25mg"] },
  { key: "ozempic", label: "Ozempic", doseOptions: ["0.25mg", "0.5mg", "1mg"] },
  { key: "saxenda", label: "Saxenda", doseOptions: ["0.6mg", "1.2mg", "1.8mg", "2.4mg", "3.0mg"] },
  { key: "rybelsus", label: "Rybelsus", doseOptions: [] },
  { key: "none", label: "None of the above", doseOptions: [] },
];

// Form B Q1 — "Which medication are you currently using?" Exact labels and order as
// printed in the Form B doc (includes the generic drug name per option, unlike the
// shorter Q13 list above). Keys line up with GLP1_MEDICATIONS so the dose ladder on
// Q2 (current-dose.jsx) can still be looked up by key.
export const FORM_B_CURRENT_MEDICATIONS = [
  { key: "mounjaroInjection", label: "Mounjaro (tirzepatide) injection" },
  { key: "wegovyInjection", label: "Wegovy (semaglutide) injection" },
  { key: "ozempic", label: "Ozempic (semaglutide) injection" },
  { key: "rybelsus", label: "Rybelsus (Semaglutide) tablet" },
  { key: "wegovyTablet", label: "Wegovy (semaglutide) tablet" },
  { key: "saxenda", label: "Saxenda (liraglutide)" },
  { key: "other", label: "Other" },
];

// Side effects checklist shared by Form B's Q5 and the kept GLP-1 follow-up pathway.
export const SIDE_EFFECTS = [
  "Nausea",
  "Vomiting",
  "Diarrhoea",
  "Constipation",
  "Headache",
  "Fatigue",
  "Energy Loss",
  "Muscles Loss",
  "Abdominal Pain",
  "Others",
];

export const SIDE_EFFECT_SEVERITY = [
  { value: 1, label: "1 - Mild", tier: TIER.GREEN },
  { value: 2, label: "2 - Moderate", tier: TIER.GREEN, orangeCandidate: true },
  { value: 3, label: "3 - Uncomfortable", tier: TIER.AMBER, orangeCandidate: true },
  { value: 4, label: "4 - Strong", tier: TIER.AMBER },
  { value: 5, label: "5 - Severe", tier: TIER.AMBER },
];

// How long since the most recent dose — used both by the kept Q16 question and to
// decide whether a "current" GLP-1 user's gap is large enough to need re-titration.
export const LAST_DOSE_GAP = [
  { key: "lt7", label: "Less than 7 days ago", tier: TIER.GREEN },
  { key: "d7to14", label: "7 to 14 days ago", tier: TIER.GREEN },
  { key: "d15to28", label: "15 to 28 days ago", tier: TIER.AMBER },
  { key: "gt28", label: "More than 28 days ago", tier: TIER.AMBER, orangeCandidate: true, forcedDoseReset: true },
];

export const WEIGHT_LOSS_REASONS = [
  "Improving my overall health",
  "Looking and feeling better",
  "Avoiding or managing a specific health condition",
  "Becoming more active",
  "Improving my mood and mental well being",
  "Improving my sleep or energy levels",
];

export const STRUGGLES_OPTIONS = [
  "Sugary Foods e.g. cakes, cookies, donuts etc",
  "Carb rich Foods e.g. bread, pasta, rice etc",
  "Cravings",
  "Boredom Eating",
  "Stress/Emotional Eating",
  "Eating late in the evenings / night time",
  "Snacking all the time",
];

// BMI thresholds: [ethnicity bucket][hasComorbidity]
export const BMI_THRESHOLDS = {
  white: { withoutComorbidity: 30, withComorbidity: 27 },
  other: { withoutComorbidity: 27.5, withComorbidity: 24.5 },
};

// Existing GLP-1 treatment continuation floor (doc: "If you have already started
// weight-loss injection treatment, you can continue treatment until a minimum BMI of 22").
export const CONTINUING_TREATMENT_BMI_FLOOR = 22;

export const ETHNICITY_WHITE_LABEL = "White";

export function ethnicityBucket(ethnicity) {
  return ethnicity === ETHNICITY_WHITE_LABEL ? "white" : "other";
}
